import { AfterContentInit, Component, ContentChildren, ElementRef, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID, QueryList, TemplateRef, ViewChild } from '@angular/core';
import { UserRoleName } from '../../models/user/user-role.model';
import { User } from '../../models/user/user.model';
import { AuthService } from '../../services/auth.service';
import { UploadComponentActionName, UploadComponentType } from '../upload/upload.component';
import { isPlatformBrowser } from '@angular/common';
import { SectionDirective } from '../../directives/section.directive';

declare var bootstrap: any;

export interface MenuActionsComponentAction {
    text?: string;
    link?: string;
    external_link?: string;
    icon?: string;
    highlight?: boolean;
    roles?: UserRoleName[];
    divider?: boolean;
    header?: boolean;
    id?: string;
    hide?: boolean;
    disabled?: boolean;
    queryParams?: any;
    tooltip?: string;
    file?: UploadComponentType|null;
    template?: string;
};

@Component({
    selector: 'menu-actions',
    templateUrl: './menu-actions.component.html',
    styleUrls: ['./menu-actions.component.scss'],
})
export class MenuActionsComponent implements OnInit, AfterContentInit {
    @ViewChild('dropdownButton') dropdownButton!: ElementRef;

    @ContentChildren(SectionDirective) templates?: QueryList<any>;
    templatesList: {[key: string]: TemplateRef<any>} = {};

    protected innerActions: MenuActionsComponentAction[] = [];
    protected innerSeparatorsBetween: boolean = true;
    protected innerDropdownStartFrom: number = 1;

    user: User|null = null;
    actionsList: MenuActionsComponentAction[] = [];

    @Output() buttonClicked: EventEmitter<string> = new EventEmitter();
    @Output() onFileEvent: EventEmitter<{id?: string, action: UploadComponentActionName, response: any}> = new EventEmitter();

    @Input() defaultEmptyIcon: string = 'fa-solid';
    @Input() smallButtons: boolean = true;
    @Input() dropdownOnly: boolean = false;
    @Input() dropdownIcon: string = 'fa-bars';
    @Input() dropdownClass: string = 'btn-primary';
    @Input() dropdownButtonText: string = '';
    @Input() roles: UserRoleName[]  = ['admin','marketing','partner','student','teacher'];
    @Input() rightOffset: boolean = true;
    @Input()
    set actions(value: MenuActionsComponentAction[]) {
        if (this.innerActions !== value) {
            this.innerActions = value?.slice() || [];
            this.computeActions();
        }
    }
    get actions(): MenuActionsComponentAction[] {
        return this.innerActions;
    }

    @Input()
    set separatorsBetween(value: boolean) {
        if (this.innerSeparatorsBetween !== value) {
            this.innerSeparatorsBetween = value;
            this.computeActions();
        }
    }
    get separatorsBetween(): boolean {
        return this.innerSeparatorsBetween;
    }

    @Input()
    set dropdownStartFrom(value: number) {
        if (this.innerDropdownStartFrom !== value) {
            this.innerDropdownStartFrom = value;
            this.computeActions();
        }
    }
    get dropdownStartFrom(): number {
        return this.innerDropdownStartFrom;
    }

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private authService: AuthService,
    ) {
        this.user = this.authService.getUser() ?? this.user;
    }

    ngOnInit(): void {

    }

    ngAfterContentInit() {
        this.templates?.forEach((item) => {
            this.templatesList[item.getType()] = item.template;
        });
    }

    ngAfterViewInit() {
        this.dropdownButton?.nativeElement && new bootstrap.Dropdown(this.dropdownButton?.nativeElement, {
            popperConfig(defaultBsPopperConfig: any) {
                return { ...defaultBsPopperConfig, strategy: 'fixed' };
            }
        });
    }

    onButtonClicked(event: Event, buttonId: string): boolean {
        event?.stopPropagation();
        this.buttonClicked.emit(buttonId);

        const dropdown = bootstrap.Dropdown.getInstance(this.dropdownButton.nativeElement);
        if (dropdown) {
            dropdown.hide();
        }

        return false;
    }

    onUploadHandle(buttonId: string, action: UploadComponentActionName, event: any) {
        this.onFileEvent.emit({
            id: buttonId,
            action,
            response: event
        });
    }

    computeActions(): void {
        let actions = this?.actions
            ?.filter(item => {
                return !item.hide
                    && (!this.user?.role?.name
                        || !item?.roles?.length
                        || item?.roles?.includes(this.user?.role?.name))
            })
            ?.slice() || [];

        if (this.separatorsBetween) {
            let dropdownActions = actions.slice(this.dropdownStartFrom);

            dropdownActions = dropdownActions
                ?.map(item => {
                    delete item.divider;
                    return item;
                })
                ?.filter(item => Object.keys(item).length > 0)
                ?.reduce((acc, item, idx) => {
                    acc.push({ ...item, divider: false });

                    if (idx < dropdownActions?.length - 1) {
                        acc.push({ divider: true } as MenuActionsComponentAction);
                    }
                    return acc;
                }, [] as MenuActionsComponentAction[])
            ;

            actions = [
                ...actions.slice(0, this.dropdownStartFrom),
                ...dropdownActions
            ];

        }

        this.actionsList = actions.slice();
    }

    onDropdownButton(event: Event, currentButton: HTMLElement): void {
        event?.stopPropagation();

        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const openDropdowns = document.querySelectorAll('.dropdown-toggle.show');
        openDropdowns?.forEach(toggle  => {
            if (toggle !== currentButton) {
                const dropdown = bootstrap.Dropdown.getInstance(toggle);
                dropdown && dropdown.hide();
            }
        });
    }

}
